'use client'

import React, { useEffect, useState } from 'react'
import { usePushSubscription } from '@/hooks/usePushSubscription'

/**
 * Debug component to help diagnose push notification issues.
 * Shows detailed information about service worker, permission, and subscription state.
 * Hidden by default - press Ctrl+Shift+D to toggle visibility.
 */
export function PushSubscriptionDebug() {
  const {
    permission,
    subscription,
    isSubscribed,
    isLoading,
    error,
    isSupported,
  } = usePushSubscription()

  const [swStatus, setSwStatus] = useState<string>('Checking...')
  const [vapidKey, setVapidKey] = useState<string>('Checking...')
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    // Secret hotkey to toggle debug panel (Ctrl+Shift+D)
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault()
        setIsVisible(prev => !prev)
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [])

  useEffect(() => {
    // Check service worker status
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        if (registrations.length === 0) {
          setSwStatus('❌ No service workers registered')
        } else {
          const swInfo = registrations.map((reg, i) =>
            `✅ SW ${i + 1}: ${reg.active?.scriptURL || 'installing'}`
          ).join(', ')
          setSwStatus(swInfo)
        }
      })
    } else {
      setSwStatus('❌ Service Worker not supported')
    }

    // Check VAPID key
    const key = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY
    if (key) {
      setVapidKey(`✅ Present (${key.substring(0, 20)}...)`)
    } else {
      setVapidKey('❌ MISSING - VAPID key not found!')
    }
  }, [])

  // Don't render anything if not visible
  if (!isVisible) {
    return null
  }

  if (!isSupported) {
    return (
      <div className="fixed bottom-4 left-4 bg-red-100 border border-red-300 rounded-lg p-4 max-w-md text-xs z-50">
        <h3 className="font-bold text-red-900 mb-2">Push Not Supported</h3>
        <p className="text-red-700">Your browser doesn&apos;t support push notifications.</p>
        <button
          onClick={() => setIsVisible(false)}
          className="mt-2 text-xs text-red-600 hover:text-red-800 underline"
        >
          Close (or press Ctrl+Shift+D)
        </button>
      </div>
    )
  }

  return (
    <div className="fixed bottom-4 left-4 bg-slate-100 border border-slate-300 rounded-lg p-4 max-w-md text-xs font-mono z-50">
      <h3 className="font-bold text-slate-900 mb-2 font-sans">🔍 Push Notification Debug</h3>

      <div className="space-y-1 text-slate-700">
        <div><strong>Browser Support:</strong> {isSupported ? '✅ Yes' : '❌ No'}</div>
        <div><strong>Permission:</strong> {permission === 'granted' ? '✅' : permission === 'denied' ? '❌' : '⏸️'} {permission}</div>
        <div><strong>Subscribed:</strong> {isSubscribed ? '✅ Yes' : '❌ No'}</div>
        <div><strong>Loading:</strong> {isLoading ? '⏳ Yes' : 'No'}</div>
        <div><strong>Error:</strong> {error || 'None'}</div>
        <div><strong>Service Worker:</strong> {swStatus}</div>
        <div><strong>VAPID Key:</strong> {vapidKey}</div>
        {subscription && (
          <div className="mt-2 pt-2 border-t border-slate-300">
            <div><strong>Endpoint:</strong> {subscription.endpoint.substring(0, 50)}...</div>
          </div>
        )}
      </div>

      <div className="mt-2 flex gap-2">
        <button
          onClick={() => {
            console.log('=== Push Subscription Debug Info ===')
            console.log('Permission:', permission)
            console.log('Subscribed:', isSubscribed)
            console.log('Subscription:', subscription)
            console.log('Error:', error)
            console.log('VAPID Key:', process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY)
            console.log('Service Worker:', navigator.serviceWorker?.controller)
          }}
          className="flex-1 px-2 py-1 bg-slate-200 hover:bg-slate-300 rounded text-slate-800 font-sans"
        >
          Log to Console
        </button>
        <button
          onClick={() => setIsVisible(false)}
          className="px-2 py-1 bg-slate-200 hover:bg-slate-300 rounded text-slate-800 font-sans"
          title="Close (or press Ctrl+Shift+D)"
        >
          ✕
        </button>
      </div>
    </div>
  )
}
